// Таблицата на справката „Наличности": ниски редове на един ред. Широк екран —
// # · Продукт · Група · Мин. · Ср. цена · Посл. цена · Наличност · Нето ·
// Бруто; лаптоп/таблет — групата и минимумът приглушено до името, последната
// цена — до средната; телефон — карти. Филтрите са в заглавията (Група —
// на средна ширина в „Продукт"; Наличност — под/до/над минимума); заглавията
// сортират; редът „Общо" е за всички показани (не само за видимите преди
// „Покажи още"). Количествата са подравнени по десетичната запетая
// (`stockQtyLayout` — за целия филтриран резултат). ↑/↓ по редовете, Enter (или клик) вика `onOpen(ред)`. До
// името — чипът „изтрит" (продукт, изтрит в организацията) и значката
// „⇄ заместил / заместен с" за периода на замените.
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { formatMoney } from "../../components/numbers";
import { formatQty, splitQty } from "../../components/qty";
import { DeletedProductChip } from "../../components/product/DeletedProductChip";
import { sectionList, type ColumnSections } from "../../components/filters/useColumnFilters";
import type { Period } from "../../components/period/periodPresets";
import { StockSubstitutionBadge } from "../stockCard/StockSubstitutionBadge";
import type { ProductSubstitutionSummary } from "../stockCard/stockSubstitutionModel";
import { EmptyDash, ReportHead } from "./ReportListBits";
import {
  REPORT_HEAD_CLASS,
  REPORT_HEADER_ROW_CLASS,
  REPORT_OPENABLE_ROW_CLASS,
  REPORT_TOTAL_ROW_CLASS,
  moveRowFocus,
  openableRowProps,
} from "./reportListModel";
import {
  groupOf,
  type StockFilterKey,
  type StockQtyLayout,
  type StockRow,
  type StockSort,
  type StockSortKey,
} from "./stockShared";

export type StockTableDensity = "full" | "medium" | "cards";

// Литерални класове — Tailwind сканира само пълни низове
const GRID_CLASS = {
  full: "grid grid-cols-[40px_minmax(180px,1.6fr)_minmax(96px,1fr)_136px_112px_120px_160px_104px_110px] items-center gap-2.5 px-[18px]",
  medium: "grid grid-cols-[36px_minmax(170px,1.5fr)_170px_150px_100px_108px] items-center gap-2.5 px-[18px]",
} as const;

// Колоните преди Нето — етикетът „Общо" ги заема
const TOTAL_LABEL_SPAN = { full: "col-span-7", medium: "col-span-4" } as const;

// Количество в зони: цяла част (дясно) · запетая с дробната част · мерна
// единица. С `layout` дробната зона и мерната единица са с еднаква ширина в
// цялата колона (в `ch` — шрифтът е моноширинен), затова запетаите и мерните
// единици застават една под друга при 0–4 знака; цяло число оставя дробната
// зона празна. Без `layout` (картите на телефон) — просто число и мярка.
const QtyCell = ({
  value,
  unit,
  decimals,
  toneClass = "text-ink-2",
  layout,
}: {
  value: number;
  unit: string | null;
  decimals: number;
  toneClass?: string;
  layout?: StockQtyLayout;
}) => {
  if (value === 0) return <EmptyDash />;
  if (!layout)
    return (
      <span className="inline-flex items-baseline justify-end whitespace-nowrap font-sheet tabular-nums">
        <span className={`text-right ${toneClass}`}>{formatQty(value, decimals)}</span>
        <span className="ml-1.5 text-left font-normal text-muted">{unit ?? ""}</span>
      </span>
    );
  const { integer, fraction } = splitQty(value, decimals);
  const unitText = unit ?? "";
  return (
    <span className="inline-flex items-baseline justify-end whitespace-nowrap font-sheet tabular-nums">
      <span className={`text-right ${toneClass}`}>{integer}</span>
      {layout.fractionChars > 0 && (
        <span className={`shrink-0 text-left ${toneClass}`} style={{ width: `${layout.fractionChars}ch` }}>
          {fraction}
        </span>
      )}
      {layout.unitChars > 0 && (
        <span
          title={unitText.length > layout.unitChars ? unitText : undefined}
          className="ml-1.5 shrink-0 truncate text-left font-normal text-muted"
          style={{ width: `${layout.unitChars}ch` }}
        >
          {unitText}
        </span>
      )}
    </span>
  );
};

// Минусът от продажби до нулата: „−10 продадени без наличност"
const SoldWithoutStock = ({ row, className = "" }: { row: StockRow; className?: string }) => {
  const { t } = useTranslation();
  if (!row.soldWithoutStock) return null;
  return (
    <span title={t("inventoryPage.wsSoldWithoutStockHint")} className={`text-[11px] font-semibold text-danger-ink ${className}`}>
      {t("inventoryPage.wsSoldWithoutStock", { qty: formatQty(row.soldWithoutStock, row.decimals) })}
    </span>
  );
};

// Наличността е акцентът на реда; под минимума — червена
const qtyToneOf = (row: StockRow) =>
  row.bucket === "under" && row.minQty ? "font-bold text-danger-ink" : "font-bold text-brand-deep-ink";

const Money = ({ value, strong = false }: { value: number | null; strong?: boolean }) =>
  value == null || value === 0 ? (
    <EmptyDash />
  ) : (
    <span className={`font-sheet tabular-nums ${strong ? "font-bold text-ink" : "text-ink-2"}`}>
      {formatMoney(value)}
    </span>
  );

type Props = {
  rows: StockRow[];
  density: StockTableDensity;
  sort: StockSort | null;
  onSort: (key: StockSortKey) => void;
  filters: ColumnSections<StockFilterKey>;
  // Клик/Enter на ред — какво показва продуктът (движения, карта…)
  onOpen: (row: StockRow) => void;
  totals: { net: number; gross: number };
  // Подравняването на количествата (`stockQtyLayout` за целия филтриран резултат)
  qtyLayout: StockQtyLayout;
  footer?: ReactNode;
  // „⇄" до името: замените на продукта за периода (useStockSubstitutionSummary)
  substitutions?: Map<number, ProductSubstitutionSummary>;
  substitutionPeriod?: Period;
};

export const StockReportTable = ({
  rows,
  density,
  sort,
  onSort,
  filters,
  onOpen,
  totals,
  qtyLayout,
  footer,
  substitutions,
  substitutionPeriod,
}: Props) => {
  const { t } = useTranslation();
  const badgeOf = (row: StockRow) =>
    substitutions && substitutionPeriod ? (
      <StockSubstitutionBadge
        summary={substitutions.get(row.productId)}
        period={substitutionPeriod}
        decimals={row.decimals}
      />
    ) : null;

  if (density === "cards") {
    return (
      <div onKeyDown={moveRowFocus}>
        {rows.map((row) => (
          <div
            key={row.productId}
            data-product-id={row.productId}
            {...openableRowProps(() => onOpen(row))}
            className="flex cursor-pointer flex-col gap-[5px] border-t border-shade-1 px-3.5 py-3 outline-none focus-visible:bg-fill"
          >
            <div className="flex items-baseline gap-2 text-sm">
              <span className={`min-w-0 flex-1 truncate font-semibold ${row.deleted ? "text-body" : "text-ink"}`}>
                {row.name}
              </span>
              {row.deleted && <DeletedProductChip />}
              {badgeOf(row)}
              <QtyCell value={row.quantity} unit={row.unit} decimals={row.decimals} toneClass={qtyToneOf(row)} />
            </div>
            <div className="flex items-baseline justify-between gap-2.5 text-[12.5px] text-body">
              <span className="min-w-0 truncate">
                {groupOf(row)}
                {row.minQty != null &&
                  ` · ${t("inventoryPage.stockMinLine", { qty: formatQty(row.minQty, row.decimals) })}`}
                <SoldWithoutStock row={row} className="ml-1.5" />
              </span>
              <span className="text-sm">
                <Money value={row.sumWithVat} strong />
              </span>
            </div>
          </div>
        ))}
        {footer}
        <div className="flex items-baseline justify-between gap-2.5 border-t border-shade-2 bg-fill px-3.5 py-3">
          <span className={REPORT_HEAD_CLASS}>{t("inventoryPage.total")}</span>
          <span className="font-sheet font-bold text-brand-deep-ink">{formatMoney(totals.gross)}</span>
        </div>
      </div>
    );
  }

  const grid = GRID_CLASS[density];
  const full = density === "full";
  const head = (
    label: string,
    options: { sortKey?: StockSortKey; align?: "right"; filters?: ReturnType<typeof sectionList> } = {},
  ) => <ReportHead label={label} sort={sort} onSort={onSort} wrap {...options} />;

  return (
    <div className="overflow-x-auto">
      <div className={full ? "min-w-[1176px]" : "min-w-[780px]"} onKeyDown={moveRowFocus}>
        <div className={`${grid} ${REPORT_HEADER_ROW_CLASS}`}>
          {head("#")}
          {head(t("inventoryPage.colProductName"), {
            sortKey: "name",
            filters: full ? undefined : sectionList(filters.groups),
          })}
          {full && (
            <>
              {head(t("inventoryPage.colGroup"), { sortKey: "group", filters: sectionList(filters.groups) })}
              {head(t("inventoryPage.colMinQty"), { sortKey: "min", align: "right" })}
            </>
          )}
          {head(t("inventoryPage.colAvgPrice"), { sortKey: "avgPrice", align: "right" })}
          {full && head(t("inventoryPage.colLastPriceShort"), { sortKey: "lastPrice", align: "right" })}
          {head(t("inventoryPage.colAvailable"), { sortKey: "qty", align: "right", filters: sectionList(filters.buckets) })}
          {head(t("inventoryPage.colNet"), { sortKey: "net", align: "right" })}
          {head(t("inventoryPage.colGross"), { sortKey: "gross", align: "right" })}
        </div>
        {rows.map((row, index) => (
          <div
            key={row.productId}
            data-product-id={row.productId}
            {...openableRowProps(() => onOpen(row))}
            className={`${grid} ${REPORT_OPENABLE_ROW_CLASS}`}
          >
            <span className="font-sheet text-faint">{index + 1}</span>
            <span className="flex min-w-0 items-baseline gap-2">
              <span className={`min-w-0 truncate font-semibold ${row.deleted ? "text-body" : "text-ink"}`}>
                {row.name}
              </span>
              {row.deleted && <DeletedProductChip />}
              {badgeOf(row)}
              {!full && (
                <span className="min-w-0 shrink-[2] truncate text-muted">
                  · {groupOf(row)}
                  {row.minQty != null &&
                    ` · ${t("inventoryPage.stockMinLine", { qty: formatQty(row.minQty, row.decimals) })}`}
                </span>
              )}
            </span>
            {full && (
              <>
                <span className="truncate text-ink-2">{groupOf(row)}</span>
                <span className="text-right">
                  {row.minQty == null ? (
                    <EmptyDash />
                  ) : (
                    <QtyCell value={row.minQty} unit={row.unit} decimals={row.decimals} layout={qtyLayout} />
                  )}
                </span>
              </>
            )}
            <span className="flex items-baseline justify-end gap-1.5 whitespace-nowrap">
              <Money value={row.weightedAvgPrice} />
              {!full && row.lastDeliveryPrice != null && (
                <span className="font-sheet text-xs text-muted">
                  {t("inventoryPage.stockLastPriceLine", { price: formatMoney(row.lastDeliveryPrice) })}
                </span>
              )}
            </span>
            {full && (
              <span className="text-right">
                <Money value={row.lastDeliveryPrice} />
              </span>
            )}
            <span className="flex min-w-0 flex-col items-end text-right">
              <QtyCell
                value={row.quantity}
                unit={row.unit}
                decimals={row.decimals}
                toneClass={qtyToneOf(row)}
                layout={qtyLayout}
              />
              {/* може да е по-дълъг от колоната — пренася се, не влиза в съседната */}
              <SoldWithoutStock row={row} className="max-w-full leading-tight" />
            </span>
            <span className="text-right">
              <Money value={row.sumNoVat} />
            </span>
            <span className="text-right">
              <Money value={row.sumWithVat} strong />
            </span>
          </div>
        ))}
        {footer}
        {rows.length > 0 && (
          <div className={`${grid} ${REPORT_TOTAL_ROW_CLASS}`}>
            <span className={`${REPORT_HEAD_CLASS} ${TOTAL_LABEL_SPAN[density]}`}>{t("inventoryPage.total")}</span>
            <span className="text-right font-sheet font-semibold text-ink">{formatMoney(totals.net)}</span>
            <span className="text-right font-sheet font-bold text-brand-deep-ink">{formatMoney(totals.gross)}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default StockReportTable;
